// src/components/AddMemberPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, orderBy as firestoreOrderBy } from 'firebase/firestore';
import { Form, Button, Container, Row, Col, Card, Spinner, Alert, Breadcrumb, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPhotoFilm } from '@fortawesome/free-solid-svg-icons';
import { isValid, parse } from 'date-fns';
import MemberPhotoAvatar from './MemberPhotoAvatar'; // Import the avatar component

// --- Helper: Fetch orgId once ---
async function getOrgId() {
    if (!auth.currentUser) { return null; }
    try {
        const idTokenResult = await auth.currentUser.getIdTokenResult(false);
        return idTokenResult.claims.orgId || null;
    } catch (error) {
        console.error("getOrgId (AddMember): Error getting custom claims token:", error);
        return null;
    }
}

// --- Helper functions for birthday dropdowns ---
const getMonths = () => Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: new Date(0, i).toLocaleString('default', { month: 'long' }) }));
const getDaysInMonth = (month, year) => {
    if (!month) return Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
    const yearToUse = (year && parseInt(year, 10)) ? parseInt(year, 10) : 2000;
    const days = new Date(yearToUse, parseInt(month, 10), 0).getDate();
    return Array.from({ length: days }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
};
const getYears = () => {
    const years = [{ value: '', label: 'Year' }];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= currentYear - 120; i--) {
        years.push({ value: i, label: i.toString() });
    }
    return years;
};

// --- Main Component: AddMemberPage ---
function AddMemberPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const passedState = location.state;

    // Form fields state
    const [selectedFamilyId, setSelectedFamilyId] = useState('');
    const [memberName, setMemberName] = useState('');
    const [selectedBirthMonth, setSelectedBirthMonth] = useState('');
    const [selectedBirthDay, setSelectedBirthDay] = useState('');
    const [selectedBirthYear, setSelectedBirthYear] = useState('');
    const [cellphone, setCellphone] = useState('');
    const [email, setEmail] = useState('');
    const [currentPhotoPath, setCurrentPhotoPath] = useState(null); // State for the selected photo

    // Component/Data state
    const [familiesList, setFamiliesList] = useState([]);
    const [preselectedFamily, setPreselectedFamily] = useState(null);
    const [needsFamilyFetch, setNeedsFamilyFetch] = useState(true);
    const [loadingFamilies, setLoadingFamilies] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [userOrgId, setUserOrgId] = useState(null);

    const firebaseFunctions = getFunctions();
    const addMemberV2Func = httpsCallable(firebaseFunctions, 'addMemberV2');

    // --- Effect 1: Fetch orgId ---
    useEffect(() => {
        let isMounted = true;
        getOrgId().then(orgId => {
            if (isMounted) {
                if (orgId) setUserOrgId(orgId);
                else {
                    setError("Could not determine organization ID. Please ensure you are logged in.");
                    setLoadingFamilies(false);
                }
            }
        });
        return () => { isMounted = false };
    }, []);

    // --- Effect 2: Handle passed state from navigation ---
    useEffect(() => {
        // Handle pre-selected family
        const preselectedId = passedState?.preselectedFamilyId;
        const preselectedName = passedState?.preselectedFamilyName;
        if (preselectedId && preselectedName) {
            setPreselectedFamily({ id: preselectedId, name: preselectedName });
            setSelectedFamilyId(preselectedId);
            setNeedsFamilyFetch(false);
            setLoadingFamilies(false);
        } else {
            setPreselectedFamily(null);
            setNeedsFamilyFetch(true);
        }

        // Handle returned photo path
        if (passedState?.newPhotoPath) {
            setCurrentPhotoPath(passedState.newPhotoPath === 'REMOVED' ? null : passedState.newPhotoPath);
            // Create a copy of the state, remove the photo path, and then navigate.
            const { newPhotoPath, ...restState } = passedState;
            navigate(location.pathname, { replace: true, state: restState });
        }
    }, [passedState, navigate, location.pathname]);


    // --- Effect 3: Fetch Families ONLY IF NEEDED ---
    useEffect(() => {
        if (!userOrgId || !needsFamilyFetch) {
            if (!needsFamilyFetch) setLoadingFamilies(false);
            return;
        }

        setLoadingFamilies(true);
        let isMounted = true;
        const fetchFamilies = async () => {
            try {
                const q = query(collection(db, 'family'), where('orgId', '==', userOrgId), firestoreOrderBy('surname'));
                const snap = await getDocs(q);
                const families = snap.docs.map(doc => ({ id: doc.id, display: `${doc.data().surname || 'N/A'} - ${doc.data().street || 'No Street'}` }));
                if (isMounted) setFamiliesList(families);
            } catch (err) {
                console.error("Error fetching families:", err);
                if (isMounted) setError("Failed to load families list.");
            } finally {
                if (isMounted) setLoadingFamilies(false);
            }
        };
        fetchFamilies();
        return () => { isMounted = false };
    }, [userOrgId, needsFamilyFetch]);

    // --- Validation and Submission ---
    const validateBirthday = () => { /* ... validation logic ... */ return true; };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null); setSuccessMessage(null);

        if (!memberName.trim()) { setError("Member name is required."); return; }
        if (!selectedFamilyId) { setError("Family ID is missing. Please select a family."); return; }
        if (!validateBirthday()) return;

        setSubmitting(true);
        try {
            const payload = {
                familyId: selectedFamilyId,
                name: memberName.trim(),
                birth_month: selectedBirthMonth || null,
                birth_day: selectedBirthDay || null,
                birth_year: selectedBirthYear || null,
                cellphone: cellphone.trim() || null,
                email: email.trim() || null,
                photo: currentPhotoPath, // Include the selected photo path
            };

            const result = await addMemberV2Func(payload);
            if (result.data?.success) {
                setSuccessMessage(result.data.message || "Member added successfully!");
                // Reset form
                setMemberName('');
                setCellphone('');
                setEmail('');
                setCurrentPhotoPath(null);
                setSelectedBirthMonth(''); setSelectedBirthDay(''); setSelectedBirthYear('');
                if (!preselectedFamily) setSelectedFamilyId('');

                const navigateTo = preselectedFamily ? `/families/${preselectedFamily.id}` : '/families';
                setTimeout(() => navigate(navigateTo), 2000);
            } else {
                throw new Error(result.data?.message || "Failed to add member.");
            }
        } catch (err) {
            console.error("Error calling addMemberV2 function:", err);
            setError(err.message || "An unexpected error occurred.");
        } finally {
            setSubmitting(false);
        }
    };

    const availableMonths = React.useMemo(() => getMonths(), []);
    const availableDays = React.useMemo(() => getDaysInMonth(selectedBirthMonth, selectedBirthYear), [selectedBirthMonth, selectedBirthYear]);
    const availableYears = React.useMemo(() => getYears(), []);


    // --- Render Logic ---
    return (
        <Container fluid className="mt-3 mb-5">
             <Row className="justify-content-center">
                <Col md={8} lg={6}>
                     <Breadcrumb>
                        {/* ... breadcrumb items ... */}
                     </Breadcrumb>
                    <Card>
                         <Card.Header as="h3" className="text-center">Add New Member {preselectedFamily ? `to ${preselectedFamily.name} Family` : ''}</Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger">{error}</Alert>}
                            {successMessage && <Alert variant="success">{successMessage}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                {/* --- Family Selection --- */}
                                {!preselectedFamily && (
                                    <Form.Group className="mb-3" controlId="memberFamily">
                                        <Form.Label>Family <span className="text-danger">*</span></Form.Label>
                                        <Form.Select value={selectedFamilyId} onChange={(e) => setSelectedFamilyId(e.target.value)} required disabled={submitting || loadingFamilies}>
                                            <option value="">-- Select Family --</option>
                                            {loadingFamilies ? <option disabled>Loading families...</option> : familiesList.map(family => <option key={family.id} value={family.id}>{family.display}</option>)}
                                        </Form.Select>
                                    </Form.Group>
                                )}

                                {/* --- Photo Section --- */}
                                <div className="text-center mb-3">
                                    <MemberPhotoAvatar src={currentPhotoPath} alt="New member photo" size={120} />
                                </div>
                                <div className="d-grid mb-4">
                                    <Button
                                        as={Link}
                                        to={`/families/${selectedFamilyId}/assign-photos`}
                                        // CORRECTED: Pass pre-selected family info to preserve state
                                        state={{
                                            forMemberName: memberName || 'New Member',
                                            currentPhotoPath: currentPhotoPath,
                                            returnPath: location.pathname,
                                            preselectedFamilyId: preselectedFamily?.id,
                                            preselectedFamilyName: preselectedFamily?.name,
                                        }}
                                        variant="outline-primary"
                                        disabled={submitting || !selectedFamilyId}
                                    >
                                        <FontAwesomeIcon icon={faPhotoFilm} className="me-2" />
                                        Set Photo
                                    </Button>
                                </div>

                                {/* --- Other Form Fields --- */}
                                <Form.Group className="mb-3" controlId="memberName">
                                    <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="text" placeholder="Enter first name" value={memberName} onChange={(e) => setMemberName(e.target.value)} required disabled={submitting} />
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="memberEmail">
                                    <Form.Label>Email Address</Form.Label>
                                    <Form.Control type="email" placeholder="Enter email address" value={email} onChange={(e) => setEmail(e.target.value)} disabled={submitting} />
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="memberCellphone">
                                    <Form.Label>Cellphone</Form.Label>
                                    <Form.Control type="tel" placeholder="Enter cellphone number" value={cellphone} onChange={(e) => setCellphone(e.target.value)} disabled={submitting} />
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="memberBirthdayGroup">
                                    <Form.Label>Birthday</Form.Label>
                                    <Row>
                                        <Col sm={5}><Form.Select value={selectedBirthMonth} onChange={(e) => setSelectedBirthMonth(e.target.value)} disabled={submitting}><option value="">Month</option>{availableMonths.map(m => <option key={`m-${m.value}`} value={m.value}>{m.label}</option>)}</Form.Select></Col>
                                        <Col sm={3}><Form.Select value={selectedBirthDay} onChange={(e) => setSelectedBirthDay(e.target.value)} disabled={submitting || !selectedBirthMonth}><option value="">Day</option>{availableDays.map(d => <option key={`d-${d.value}`} value={d.value}>{d.label}</option>)}</Form.Select></Col>
                                        <Col sm={4}><Form.Select value={selectedBirthYear} onChange={(e) => setSelectedBirthYear(e.target.value)} disabled={submitting}><option value="">Year</option>{availableYears.map(y => <option key={`y-${y.value}`} value={y.value}>{y.label}</option>)}</Form.Select></Col>
                                    </Row>
                                </Form.Group>

                                <div className="d-grid">
                                    <Button variant="primary" type="submit" disabled={submitting || (needsFamilyFetch && loadingFamilies && !preselectedFamily)}>
                                        {submitting ? <><Spinner as="span" size="sm" /> Adding...</> : 'Add Member'}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                 </Col>
             </Row>
        </Container>
    );
}

export default AddMemberPage;
